// @flow

import React from "react"
import { intersperse, propOr } from "ramda"
import { Link, graphql } from "gatsby"
import { Disqus } from "gatsby-plugin-disqus"
import Bio from "../components/bio"
import Layout from "../components/layout"

const BlogPostTemplate = ({ data }: { data: Object }) => {
    const post = data.markdownRemark

    const { html, excerpt, frontmatter } = post
    const { previous, next } = data

    const description = propOr(excerpt, "description", frontmatter)

    return (
        <Layout
            sidebar={false}
            showTitle={false}
            title={frontmatter.title}
            description={description}>
            <article
                itemScope
                className="blog-post"
                itemType="http://schema.org/BlogPosting">
                <header>
                    <h1 itemProp="headline">{frontmatter.title}</h1>
                    <p style={{}}>
                        <small
                            itemProp="datePublished"
                            style={{ marginRight: 10 }}>
                            <i className="fa fa-calendar" /> {frontmatter.date}
                        </small>
                        <small style={{ marginRight: 10 }}>
                            <i className="fa fa-user" /> {frontmatter.author}
                        </small>
                        <small>
                            <i className="fa fa-tags" />{" "}
                            {intersperse(
                                ", ",
                                frontmatter.tags.map((t) => (
                                    <Link
                                        key={t}
                                        to={`/tags/${encodeURIComponent(t)}`}>
                                        {t}
                                    </Link>
                                ))
                            )}
                        </small>
                    </p>
                </header>
                <section
                    itemProp="articleBody"
                    dangerouslySetInnerHTML={{ __html: html }}
                />
                <DisqusComments post={post} />
                <footer style={{ marginTop: 100 }}>
                    <Bio />
                </footer>
            </article>
            <nav className="blog-post-nav blue lighten-2 z-depth-0">
                <ul
                    style={{
                        display: `flex`,
                        flexWrap: `wrap`,
                        justifyContent: `space-between`,
                        listStyle: `none`,
                        padding: 0,
                    }}>
                    <li>
                        {previous && (
                            <Link to={previous.fields.slug} rel="prev">
                                ← {previous.frontmatter.title}
                            </Link>
                        )}
                    </li>
                    <li>
                        {next && (
                            <Link to={next.fields.slug} rel="next">
                                {next.frontmatter.title} →
                            </Link>
                        )}
                    </li>
                </ul>
            </nav>
        </Layout>
    )
}

export default BlogPostTemplate

export const pageQuery = graphql`
    query BlogPostBySlug(
        $id: String!
        $previousPostId: String
        $nextPostId: String
    ) {
        markdownRemark(id: { eq: $id }) {
            id
            excerpt
            html
            fields {
                slug
            }
            frontmatter {
                tags
                title
                author
                description
                date(formatString: "MMMM DD, YYYY")
            }
        }
        previous: markdownRemark(id: { eq: $previousPostId }) {
            fields {
                slug
            }
            frontmatter {
                title
            }
        }
        next: markdownRemark(id: { eq: $nextPostId }) {
            fields {
                slug
            }
            frontmatter {
                title
            }
        }
    }
`

type DisqusCommentsProps = { post: Object }

function DisqusComments({ post }: DisqusCommentsProps) {
    // Use .html URL's to show old comments
    const disqusId = `https://simplyrets.com/blog${post.fields.slug}.html`

    const disqusConfig = {
        url: disqusId,
        identifier: disqusId,
        title: post.frontmatter.title,
    }

    return (
        <section>
            <h4>Comments</h4>
            <Disqus config={disqusConfig} />
        </section>
    )
}
